import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ClipboardList,
  Lock,
  ReceiptText,
  SquarePen,
  StickyNote,
  Unlock,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import Authorized from "components/Authorized";
import { buildPrice } from "components/helpers";
import { linkedTabSections, useLinkedTables } from "components/linkedTables";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import AuditLog from "components/views/AuditLog";
import NotesView from "components/views/NotesView";
import { noTag } from "constants/Tag";
import setPageTitle from "helpers/title";
import { useAppTabs } from "hooks/useAppTabs";
import useShowDisabledTags from "hooks/useShowDisabledTags";
import RevenueOpportunityInfoPanel from "routes/revenue_opportunity/components/RevenueOpportunityInfoPanel";
import RevenueOpportunityInvoicesTab from "routes/revenue_opportunity/components/RevenueOpportunityInvoicesTab";
import { useRevenueOpportunityNavigation } from "routes/revenue_opportunity/useRevenueOpportunityNavigation";
import ActivityService from "services/Activity";
import AuthService from "services/Auth";
import EntityService from "services/Entity";
import RecipientService from "services/Recipient";
import RevenueOpportunityService from "services/RevenueOpportunities";
import UserService from "services/User";

const RevenueOpportunity = () => {
  const { id: revenueOpportunityId } = useParams();
  const { openEdit } = useRevenueOpportunityNavigation();
  const [auditLogId, setAuditLogId] = useState(null);
  const [model, setModel] = useState({});
  const [hasNotes, setHasNotes] = useState(false);
  const [updateAuditLogFlag, setUpdateAuditLogFlag] = useState(0);
  const showDisabledTags = useShowDisabledTags();

  const tabs = useAppTabs({
    basePath: `/revenue_opportunities/${revenueOpportunityId}`,
    defaultTab: "activityLog",
  });
  const { activeTab, handleTabChange: onTabChange } = tabs;
  const { linkedTabs, isLoading: isLoadingLinkedTabs } = useLinkedTables(
    "RevenueOpportunity",
    revenueOpportunityId
  );

  const fetchNotes = useCallback(async () => {
    if (!revenueOpportunityId) return;
    const result = await ActivityService.hasNotes(
      revenueOpportunityId,
      "RevenueOpportunity"
    );
    setHasNotes(result);
  }, [revenueOpportunityId]);

  const fetchRevenueOpportunity = useCallback(async () => {
    let model =
      await RevenueOpportunityService.getRevenueOpportunityById(
        revenueOpportunityId
      );
    model.deleted_assignee = false;

    if (model.tags.length === 0) {
      model.tags = [noTag];
    }

    if (model.recipient_id) {
      const recipient = await RecipientService.get(model.recipient_id);
      model.recipient_name = recipient?.name;
    }

    const entity = await EntityService.getEntity(model.entity_id);
    model.entity_name = entity?.name;

    if (model.contact) {
      model.contact_full_name = model.contact.full_name;
    }

    if (model.value != null) {
      model.invoice_total_value = buildPrice(Number(model.value));
    }

    setModel(model);
    setPageTitle(`Revenue Opportunity ${model.id}`);
    setUpdateAuditLogFlag(prev => prev + 1);
  }, [revenueOpportunityId]);

  useEffect(() => {
    if (AuthService.isLoggedIn() && revenueOpportunityId) {
      fetchRevenueOpportunity();
      fetchNotes();
    }
  }, [revenueOpportunityId, fetchNotes, fetchRevenueOpportunity]);

  const fetchAssignee = useCallback(async () => {
    const assignee = await UserService.getUser(model.assignee_id, true);
    const assigneeName = assignee?.full_name;
    setModel(prev => ({
      ...prev,
      deleted_assignee: !!assignee?.deleted_at,
      assignee_name: assignee?.deleted_at
        ? `${assigneeName} (DELETED)`
        : assigneeName,
      assigneeName,
    }));
  }, [model.assignee_id]);

  useEffect(() => {
    if (model.assignee_id) {
      fetchAssignee();
    }
  }, [model.assignee_id, fetchAssignee]);

  const handleTabChange = useCallback(
    (e, { name, auditLogId = null }) => {
      setAuditLogId(auditLogId);
      onTabChange(undefined, { name });
    },
    [onTabChange]
  );

  // The Activity Log deep-link (set by a note's "See Original") applies once.
  // Clear it when the active section changes away from the Activity Log so a
  // later return shows the newest page, not the old entry.
  const [lastSection, setLastSection] = useState(activeTab);
  if (activeTab !== lastSection) {
    setLastSection(activeTab);
    if (activeTab !== "activityLog") {
      setAuditLogId(null);
    }
  }

  const isAdmin = UserService.isAdmin();

  const handleLockToggle = async () => {
    try {
      if (model.is_locked) {
        await RevenueOpportunityService.unlockRevenueOpportunity(
          revenueOpportunityId
        );
      } else {
        await RevenueOpportunityService.lockRevenueOpportunity(
          revenueOpportunityId
        );
      }
      fetchRevenueOpportunity();
    } catch {
      // no-op
    }
  };

  const sections = useMemo(() => {
    const items = [
      {
        key: "activityLog",
        label: "Activity Log",
        icon: ClipboardList,
        content: (
          <AuditLog
            modelId={revenueOpportunityId}
            modelType="RevenueOpportunity"
            auditLogId={auditLogId}
            updateFlag={updateAuditLogFlag}
          />
        ),
      },
      {
        key: "invoices",
        label: "Invoices",
        icon: ReceiptText,
        content: <RevenueOpportunityInvoicesTab revenueOpportunity={model} />,
      },
      {
        key: "notes",
        label: "Notes",
        icon: StickyNote,
        badge: hasNotes && activeTab !== "notes" && (
          <span
            aria-label="Has notes"
            className="tw:size-2 tw:rounded-full tw:bg-sidebar-primary"
          />
        ),
        content: (
          <NotesView
            modelType="RevenueOpportunity"
            modelId={revenueOpportunityId}
            handleTabChange={handleTabChange}
          />
        ),
      },
    ];
    items.push(...linkedTabSections(linkedTabs, revenueOpportunityId));
    return items;
  }, [
    revenueOpportunityId,
    model,
    activeTab,
    auditLogId,
    updateAuditLogFlag,
    handleTabChange,
    hasNotes,
    linkedTabs,
  ]);

  if (isEmpty(model)) {
    return null;
  }

  return (
    <SidebarDetailLayout
      sections={sections}
      tabs={tabs}
      sectionsLoading={isLoadingLinkedTabs}
      header="Revenue Opportunity"
      breadcrumb={
        <DetailBreadcrumb
          parentTo="/revenue_opportunities"
          parentLabel="All Revenue Opportunities"
          current={model.id && `Revenue opportunity (${model.id})`}
        />
      }
      title={model.id && `Revenue opportunity (${model.id})`}
      titleActions={
        <>
          {!model.is_locked && (
            <Authorized
              permissions={[ACL_RELATIONSHIPS.revenueOpportunity.edit]}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEdit(model.id)}
              >
                <SquarePen data-icon="inline-start" />
                Edit
              </Button>
            </Authorized>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleLockToggle}>
              {model.is_locked ? (
                <>
                  <Unlock data-icon="inline-start" />
                  Unlock
                </>
              ) : (
                <>
                  <Lock data-icon="inline-start" />
                  Lock
                </>
              )}
            </Button>
          )}
        </>
      }
      info={
        <RevenueOpportunityInfoPanel
          model={model}
          showDisabledTags={showDisabledTags}
          onUpdate={fetchRevenueOpportunity}
        />
      }
      infoLabel="Revenue Opportunity Details"
    />
  );
};

export default RevenueOpportunity;
